import { ComponentEnhancerFunction } from "@uniformdev/canvas";

import { ProductCatalogClient } from "@/contentful/product-catalog.tsx";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { getContentfulEnvironment } from "../../../../clients/uniform-client/enhancers/compare-plans-enhancer.ts";
import { getComparePlansDataFromGqlContent } from "../../../../uniform/components/pricing-calculator/src/client-gql/compare-plans-mapper.ts";
import { getPricingCalculatorContent } from "../../../../uniform/components/pricing-calculator/src/client-gql/fetch.ts";

import {
    getPlanLimitConfigsByUrlSlug,
    getOfferingSkusArrayByUrlSlug
} from "./mappers.ts";

import type { Plan } from "../../../../uniform/components/pricing-calculator/src/types.ts";

const PRODUCT_CATALOG_DELIVERY_KEY =
    process.env["PRODUCT_CATALOG_DELIVERY_KEY"];
const PRODUCT_CATALOG_SPACE = process.env["PRODUCT_CATALOG_SPACE"];

interface PricingCalculatorParameters {
    urlSlug: { value: string };
    componentLocale?: { value: string };
    comparePlansData?: Plan[];
}

export const pricingCalculatorEnhancer: ComponentEnhancerFunction = async ({
    component
}) => {
    const waePricingGqlEnabled = checkFeatureFlagGate(
        featureFlags.waePricingGql
    );
    const parameters =
        component.parameters as unknown as PricingCalculatorParameters;
    if (parameters?.comparePlansData || !waePricingGqlEnabled) {
        return undefined;
    }

    const urlSlug = parameters.urlSlug?.value;
    if (!urlSlug) {
        throw new Error("no urlSlug provided");
    }

    const locale = parameters.componentLocale?.value ?? "en-US";

    const content = await getPricingCalculatorContent({
        urlSlug,
        locale
    });
    if (!content) {
        throw new Error("pricing calculator content is empty");
    }

    const offeringSkusArray = getOfferingSkusArrayByUrlSlug(urlSlug);
    if (!offeringSkusArray) {
        throw new Error(`No offering SKUs found for urlSlug: "${urlSlug}"`);
    }

    const planLimitConfigs = getPlanLimitConfigsByUrlSlug(urlSlug);
    if (!planLimitConfigs) {
        throw new Error(
            `No plan limit configs found for urlSlug: "${urlSlug}"`
        );
    }

    const contentfulEnvironment = getContentfulEnvironment();
    const productCatalogClient = new ProductCatalogClient(
        String(PRODUCT_CATALOG_DELIVERY_KEY),
        String(PRODUCT_CATALOG_SPACE),
        contentfulEnvironment
    );
    const ccpPricingData = await productCatalogClient.getCCPPricingData(
        offeringSkusArray
    );

    return getComparePlansDataFromGqlContent(
        content,
        planLimitConfigs,
        ccpPricingData
    );
};
